'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function LeaseCalculator() {
  const t = useTranslations("tools.leaseCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [calculatorMode, setCalculatorMode] = useState<'fixed-rate' | 'fixed-payment'>('fixed-rate');
  const [errorMessage, setErrorMessage] = useState('');

  const [inputs, setInputs] = useState({
    assetValue: 20000,
    residualValue: 8000,
    termYears: 3,
    termMonths: 0,
    interestRate: 6,
    monthlyPayment: 400,
  });

  const [results, setResults] = useState<{
    primaryValue: string;
    primaryLabel: string;
    totalPayments: number;
    totalInterest: number;
    depreciation: number;
    principalPercent: number;
    interestPercent: number;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1e40af',
    purple: '#7c3aed',
    green: '#8bbc21',
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    const { assetValue, residualValue, termYears, termMonths, interestRate, monthlyPayment } = inputs;
    const totalMonths = (termYears * 12) + termMonths;

    if (assetValue <= 0) {
      setErrorMessage(t('errorAssetValue'));
      return;
    }

    if (residualValue < 0) {
      setErrorMessage(t('errorResidualNegative'));
      return;
    }

    if (residualValue >= assetValue) {
      setErrorMessage(t('errorResidualTooHigh'));
      return;
    }

    if (totalMonths <= 0) {
      setErrorMessage(t('errorLeaseTerm'));
      return;
    }

    const depreciation = assetValue - residualValue;

    if (calculatorMode === 'fixed-rate') {
      if (interestRate < 0) {
        setErrorMessage(t('errorInterestRate'));
        return;
      }

      const monthlyRate = interestRate / 100 / 12;
      const depreciationPayment = depreciation / totalMonths;
      const financeCharge = (assetValue + residualValue) * monthlyRate / 2;
      const calculatedMonthlyPayment = depreciationPayment + financeCharge;
      const totalPayments = calculatedMonthlyPayment * totalMonths;
      const totalInterest = totalPayments - depreciation;

      const total = depreciation + totalInterest;
      const principalPercent = (depreciation / total) * 100;
      const interestPercent = (totalInterest / total) * 100;

      setResults({
        primaryValue: formatCurrency(calculatedMonthlyPayment),
        primaryLabel: t('monthlyPayment'),
        totalPayments,
        totalInterest,
        depreciation,
        principalPercent,
        interestPercent,
      });

    } else {
      if (monthlyPayment <= 0) {
        setErrorMessage(t('errorMonthlyPayment'));
        return;
      }

      const totalPayments = monthlyPayment * totalMonths;
      const totalInterest = totalPayments - depreciation;

      if (totalInterest < 0) {
        setErrorMessage(t('errorPaymentTooLow'));
        return;
      }

      const avgBalance = (assetValue + residualValue) / 2;
      const monthlyInterestRate = (totalInterest / totalMonths) / avgBalance;
      const annualRate = monthlyInterestRate * 12 * 100;

      const total = depreciation + totalInterest;
      const principalPercent = (depreciation / total) * 100;
      const interestPercent = (totalInterest / total) * 100;

      setResults({
        primaryValue: formatNumber(annualRate) + '%',
        primaryLabel: t('interestRate'),
        totalPayments,
        totalInterest,
        depreciation,
        principalPercent,
        interestPercent,
      });
    }
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M", x, y,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(37, 99, 235, 0.1)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabButtonsStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const, marginBottom: '2rem' },
    { flexDirection: 'row' as const, marginBottom: '2rem' }
  );

  const tabBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      padding: '0.75rem 2rem',
      border: `2px solid ${colors.blue}`,
      background: isActive ? colors.blue : 'white',
      color: isActive ? 'white' : colors.blue,
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    { fontSize: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const calculatorGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', color: colors.textSecondary, fontWeight: 600 },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    {},
    {},
    {}
  );

  const inputWithPrefixStyle = {
    position: 'relative' as const,
  };

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 600,
  };

  const inputPaddedStyle = {
    ...inputStyle,
    paddingLeft: '2rem',
  };

  const inputSuffixStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 600,
  };

  const inputPaddedRightStyle = {
    ...inputStyle,
    paddingRight: '2.5rem',
  };

  const termInputsStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      marginBottom: '0.75rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #e2e8f0',
    },
    {},
    {},
    {}
  );

  const resultItemPrimaryStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white',
      borderLeft: `4px solid ${colors.blueHover}`,
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
    },
    { padding: '1.25rem', fontSize: '1rem' },
    { padding: '1.5rem', fontSize: '1.1rem' },
    { padding: '1.5rem', fontSize: '1.1rem' }
  );

  const resultLabelStyle = {
    fontWeight: 600,
    color: colors.textSecondary,
  };

  const resultLabelPrimaryStyle = {
    fontWeight: 600,
    color: 'white',
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.blue },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const resultValuePrimaryStyle = getResponsiveStyle(
    { fontWeight: 700, color: 'white' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const chartContainerStyle = getResponsiveStyle(
    { marginTop: '2rem', textAlign: 'center' as const },
    {},
    {},
    {}
  );

  const chartLegendStyle = getResponsiveStyle(
    { display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' as const },
    {},
    {},
    {}
  );

  const legendItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const legendColorStyle = {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
  };

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    borderLeft: '4px solid #dc2626',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', color: colors.textSecondary, lineHeight: 1.8 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      border: '1px solid rgba(255, 200, 180, 0.15)',
    },
    {},
    {},
    {}
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🚗 {t('toolTitle')}</h2>
          </div>

          <div style={tabButtonsStyle}>
            <button
              style={tabBtnStyle(calculatorMode === 'fixed-rate')}
              onClick={() => {
                setCalculatorMode('fixed-rate');
                setResults(null);
                setErrorMessage('');
              }}
            >
              {t('fixedRateTab')}
            </button>
            <button
              style={tabBtnStyle(calculatorMode === 'fixed-payment')}
              onClick={() => {
                setCalculatorMode('fixed-payment');
                setResults(null);
                setErrorMessage('');
              }}
            >
              {t('fixedPaymentTab')}
            </button>
          </div>

          <div style={calculatorGridStyle}>
            <div style={calculatorFormStyle}>
              <form onSubmit={handleCalculate}>
                {errorMessage && (
                  <div style={errorMessageStyle}>
                    {errorMessage}
                  </div>
                )}

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="asset-value">{t('assetValueLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="asset-value"
                      style={inputPaddedStyle}
                      value={inputs.assetValue}
                      onChange={(e) => setInputs({...inputs, assetValue: parseFloat(e.target.value) || 0})}
                      required
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="residual-value">{t('residualValueLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="residual-value"
                      style={inputPaddedStyle}
                      value={inputs.residualValue}
                      onChange={(e) => setInputs({...inputs, residualValue: parseFloat(e.target.value) || 0})}
                      required
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('leaseTermLabel')}</label>
                  <div style={termInputsStyle}>
                    <div>
                      <input
                        type="number"
                        id="term-years"
                        style={inputStyle}
                        value={inputs.termYears}
                        onChange={(e) => setInputs({...inputs, termYears: parseInt(e.target.value) || 0})}
                        min="0"
                        placeholder={t('yearsPlaceholder')}
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        id="term-months"
                        style={inputStyle}
                        value={inputs.termMonths}
                        onChange={(e) => setInputs({...inputs, termMonths: parseInt(e.target.value) || 0})}
                        min="0"
                        max="11"
                        placeholder={t('monthsPlaceholder')}
                      />
                    </div>
                  </div>
                </div>

                {calculatorMode === 'fixed-rate' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="interest-rate">{t('interestRateLabel')}</label>
                    <div style={inputWithPrefixStyle}>
                      <input
                        type="number"
                        id="interest-rate"
                        style={inputPaddedRightStyle}
                        value={inputs.interestRate}
                        onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                        step="0.01"
                        required
                      />
                      <span style={inputSuffixStyle}>%</span>
                    </div>
                  </div>
                )}

                {calculatorMode === 'fixed-payment' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="monthly-payment">{t('monthlyPaymentLabel')}</label>
                    <div style={inputWithPrefixStyle}>
                      <span style={inputPrefixStyle}>$</span>
                      <input
                        type="number"
                        id="monthly-payment"
                        style={inputPaddedStyle}
                        value={inputs.monthlyPayment}
                        onChange={(e) => setInputs({...inputs, monthlyPayment: parseFloat(e.target.value) || 0})}
                        required
                      />
                    </div>
                  </div>
                )}

                <button type="submit" style={calculateBtnStyle}>
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            {results && (
              <div style={resultsStyle}>
                <h3 style={h2Style}>{t('resultsTitle')}</h3>

                <div style={resultItemPrimaryStyle}>
                  <span style={resultLabelPrimaryStyle}>{results.primaryLabel}</span>
                  <span style={resultValuePrimaryStyle}>{results.primaryValue}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalPayments')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalPayments)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalInterest')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalInterest)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('depreciation')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.depreciation)}</span>
                </div>

                <div style={chartContainerStyle}>
                  <svg width="200" height="200" style={{ display: 'inline-block', margin: '1rem 0' }}>
                    <path
                      d={describeArc(100, 100, 70, 0, (results.principalPercent / 100) * 360)}
                      fill={colors.blue}
                    />
                    <path
                      d={describeArc(100, 100, 70, (results.principalPercent / 100) * 360, 360)}
                      fill={colors.green}
                    />
                    <circle cx="100" cy="100" r="30" fill="white" />
                    <text x="100" y="90" textAnchor="middle" fontSize="14" fontWeight="bold" fill={colors.blue}>
                      {results.principalPercent.toFixed(0)}%
                    </text>
                    <text x="100" y="110" textAnchor="middle" fontSize="12" fill="#64748b">
                      {t('principal')}
                    </text>
                  </svg>
                  <div style={chartLegendStyle}>
                    <div style={legendItemStyle}>
                      <div style={{...legendColorStyle, background: colors.blue}}></div>
                      <span>{t('principal')}</span>
                    </div>
                    <div style={legendItemStyle}>
                      <div style={{...legendColorStyle, background: colors.green}}></div>
                      <span>{t('interest')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToUseStep1')}</li>
          <li style={listItemStyle}>{t('howToUseStep2')}</li>
          <li style={listItemStyle}>{t('howToUseStep3')}</li>
          <li style={listItemStyle}>{t('howToUseStep4')}</li>
          <li style={listItemStyle}>{t('howToUseStep5')}</li>
          <li style={listItemStyle}>{t('howToUseStep6')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightsBullet1')}</li>
          <li style={listItemStyle}>{t('insightsBullet2')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph4')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle5')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph5')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('detailsAssetValue')}:</strong> {t('detailsAssetValueDesc')}</li>
          <li style={listItemStyle}><strong>{t('detailsResidualValue')}:</strong> {t('detailsResidualValueDesc')}</li>
          <li style={listItemStyle}><strong>{t('detailsLeaseTerm')}:</strong> {t('detailsLeaseTermDesc')}</li>
          <li style={listItemStyle}><strong>{t('detailsInterestRate')}:</strong> {t('detailsInterestRateDesc')}</li>
        </ul>

        <h3 style={h3Style}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('detailsScenario1')}</li>
          <li style={listItemStyle}>{t('detailsScenario2')}</li>
          <li style={listItemStyle}>{t('detailsScenario3')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion1')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer1')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion2')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer2')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion3')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer3')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion4')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer4')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion5')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer5')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faqQuestion6')}</h3>
          <p style={faqAnswerStyle}>{t('faqAnswer6')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://superworks.com/lease-calculator/" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.omnicalculator.com/finance/lease" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://finquery.com/blog/lease-vs-buy-analysis-calculator-business/" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nerdwallet.com/auto-loans/learn/nerdwallet-lease-calculator" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/lease-calculator.html" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}